import { CheckResult, ColumnType, ProcedureColumn } from "./auditSession";
import {
  AttributeTestColumnResult,
  CONTROL_EXCEPTION_DESCRIPTIONS,
  ControlExceptionType,
  PERFORMANCE_ISSUE_DESCRIPTIONS,
} from "./attributeTestColumn";
import { Citation, deserialiseCitationFromString } from "./citation";
import { AttributeTestResultSummary } from "./procedureSessionTableShell";

/**
 * Translation layer between AttributeTestColumnResult and CheckResult formats
 * This allows us to reuse existing CheckDecision/CheckResultCard components
 * while keeping the presentation layer decoupled from the data format
 */

/** The namespace an attribute-test cell's canonical `CheckResult` id carries. */
const CANONICAL_ID_PREFIX = "attr-test-id-";

/**
 * Whether a selection names an attribute-test result, in either id shape:
 * `attr-test-id-{resultId}` with a result id behind it, or the legacy
 * `attr-test-{sourceRowId}-{columnId}-{reviewOrder}`.
 *
 * This is the question `useAttributeTestData` asks of the same id, restated
 * here so a surface can decide whether to open before that hook has resolved
 * anything: everything the hook settles from the id alone is settled here, and
 * only its two data-dependent steps — finding the column, then the summary row
 * that carries the real result id — are left out. So a selection this rejects
 * is one the hook cannot resolve.
 *
 * Carrying the bare prefix is not enough: `attr-test-` alone and
 * `attr-test-id-` alone name no result.
 */
export function selectionNamesAttributeTestResult(
  checkResultId: string | undefined,
  sourceRowId: string | undefined,
): boolean {
  if (!checkResultId) return false;

  if (checkResultId.startsWith(CANONICAL_ID_PREFIX)) {
    return checkResultId.length > CANONICAL_ID_PREFIX.length;
  }

  if (!sourceRowId) return false;
  const legacyPrefix = `attr-test-${sourceRowId}-`;
  if (!checkResultId.startsWith(legacyPrefix)) return false;
  const remainder = checkResultId.substring(legacyPrefix.length);
  const lastDashIndex = remainder.lastIndexOf("-");
  if (lastDashIndex < 0) return false;
  const reviewOrder = parseInt(remainder.substring(lastDashIndex + 1), 10);
  return !Number.isNaN(reviewOrder);
}

/**
 * Converts an AttributeTestColumnResult to CheckResult format for display
 *
 * @param result - The attribute test column result from the backend
 * @param column - The procedure column configuration
 * @param sourceRowId - The source row ID for generating unique IDs
 * @returns A CheckResult that can be used with existing check result components
 */
export function attributeTestResultToCheckResult(
  result: AttributeTestColumnResult,
  column: ProcedureColumn,
  sourceRowId: string,
): CheckResult {
  // Convert inline citations to CheckResultCitation format
  const citations = mapAttributeTestCitationsToCheckResultCitations(
    result.inlineCitations,
    column.attributeId,
  );

  // Handle discriminated union based on is_conclusive
  if (result.isConclusive) {
    // Conclusive result: has outcome, control_exceptions, etc.
    const decision =
      result.outcome === "pass"
        ? "pass"
        : result.outcome === "not_applicable"
          ? "not_applicable"
          : "fail";

    // Build reason text from outcome description and mitigating factors
    let reason = result.outcomeDescription ?? "";

    if (result.evidenceSummary) {
      reason += `\n\n**Evidence Summary:**\n\n${result.evidenceSummary}`;
    }

    if (result.potentialMitigatingFactors) {
      reason += `\n\n**Potential Mitigating Factors:**\n\n${result.potentialMitigatingFactors}`;
    }

    // Add reperformance steps as context
    if (result.reperformanceSteps.length > 0) {
      reason += "\n\n**Steps Taken:**\n";
      result.reperformanceSteps.forEach((step, index) => {
        reason += `${index + 1}. **${step.stepName}:** ${step.stepDescription}\n`;
      });
    }

    // Convert control exceptions to issues
    const issues: CheckResult["issues"] = result.controlExceptions.map(
      (exception) => ({
        issueCategory: "exception" as const,
        issueSubcategory: exception,
        description:
          CONTROL_EXCEPTION_DESCRIPTIONS[exception as ControlExceptionType] ||
          exception,
      }),
    );

    // Handle outcome-based issue generation
    if (decision === "not_applicable") {
      // For N/A outcomes, add a specific issue to trigger N/A display
      issues.push({
        issueCategory: "test_outcome" as const,
        issueSubcategory: "not_applicable" as const,
        description: "Not applicable",
      });
    } else if (decision === "pass" && issues.length === 0) {
      // If passed with no exceptions, add a "no issues" entry
      issues.push({
        issueCategory: "test_outcome" as const,
        issueSubcategory: "no_issues_found" as const,
        description: "No issues found",
      });
    }

    return {
      id: result.id
        ? `attr-test-id-${result.id}`
        : `attr-test-${sourceRowId}-${column.id}-${result.reviewOrder}`,
      checkId: null,
      evalColumnId: column.id,
      reviewerType: result.reviewerType,
      userId: result.userId || null,
      decision,
      reason,
      reviewOrder: result.reviewOrder,
      lockedBy: null,
      issues,
      citations,
      columnVersion: result.columnVersion,
      runId: result.runId ?? null,
      createdAt: result.createdAt || new Date().toISOString(),
      updatedAt: result.updatedAt || new Date().toISOString(),
    };
  } else {
    // Inconclusive result: map to "requires_attention" with fail decision
    let reason = `**Testing could not be completed**\n\n${result.partialDescription}\n\n**Issues:**\n${result.performanceIssues.map((issue) => `- ${PERFORMANCE_ISSUE_DESCRIPTIONS[issue] || issue}`).join("\n")}`;

    if (result.evidenceSummary) {
      reason += `\n\n**Evidence Summary:**\n${result.evidenceSummary}`;
    }

    // Add reperformance steps to show what was attempted
    const reasonWithSteps =
      reason +
      (result.reperformanceSteps.length > 0
        ? "\n\n**Steps Attempted:**\n" +
          result.reperformanceSteps
            .map(
              (step, index) =>
                `${index + 1}. **${step.stepName}:** ${step.stepDescription}`,
            )
            .join("\n")
        : "");

    return {
      id: result.id
        ? `attr-test-id-${result.id}`
        : `attr-test-${sourceRowId}-${column.id}-${result.reviewOrder}`,
      checkId: null,
      evalColumnId: column.id,
      reviewerType: result.reviewerType,
      userId: result.userId || null,
      decision: "fail",
      reason: reasonWithSteps,
      reviewOrder: result.reviewOrder,
      lockedBy: null,
      issues:
        result.performanceIssues.length > 0
          ? result.performanceIssues.map((issue) => ({
              issueCategory: "test_outcome",
              issueSubcategory: issue,
              description:
                PERFORMANCE_ISSUE_DESCRIPTIONS[issue] || "Testing incomplete",
            }))
          : [
              {
                issueCategory: "test_outcome",
                // No stated reason is still inconclusive, not a failure: keep this
                // off "issues_found" so the cell reads Requires Attention.
                issueSubcategory: "inconclusive",
                description: "Testing could not be completed",
              },
            ],
      citations,
      columnVersion: result.columnVersion,
      runId: result.runId ?? null,
      createdAt: result.createdAt || new Date().toISOString(),
      updatedAt: result.updatedAt || new Date().toISOString(),
    };
  }
}

/**
 * Workpaper-lazy-load: cell-badge-only translator that operates on
 * `AttributeTestResultSummary` (the slim projection that ships on
 * `DrySessionSourceRow`) rather than the heavy `AttributeTestColumnResult`.
 *
 * Used by `AttrTestColumnCell` to render its `Issues Found` /
 * `No Issues Found` / `N/A` badge without firing a per-row `useSourceRow`
 * fanout. Heavy fields (`reason`, citations, `reperformanceSteps`,
 * `evidenceSummary`, `outcomeDescription`, `partialDescription`,
 * `potentialMitigatingFactors`) are deliberately empty on the produced
 * `CheckResult` — the side-panel renderers consume the per-detail hook
 * (`useAttributeTestResultDetail`) for those fields, not the badge cell.
 *
 * The returned `CheckResult.id` mirrors
 * `attributeTestResultToCheckResult`'s pattern:
 *   - `attr-test-id-{result.id}` is the canonical form
 *   - the legacy `attr-test-{sourceRowId}-{columnId}-{reviewOrder}` shape is
 *     resolved separately in `useAttributeTestData` via a summary-array
 *     lookup before the detail hook fires.
 */
export function attributeTestSummaryToCheckResult(
  summary: AttributeTestResultSummary,
  column: ProcedureColumn,
): CheckResult {
  const isConclusive = summary.isConclusive;

  if (isConclusive) {
    const decision: CheckResult["decision"] =
      summary.outcome === "pass"
        ? "pass"
        : summary.outcome === "not_applicable"
          ? "not_applicable"
          : "fail";

    const issues: CheckResult["issues"] = summary.controlExceptions.map(
      (exception) => ({
        issueCategory: "exception",
        issueSubcategory: exception,
        description: CONTROL_EXCEPTION_DESCRIPTIONS[exception] || exception,
      }),
    );

    if (decision === "not_applicable") {
      issues.push({
        issueCategory: "test_outcome",
        issueSubcategory: "not_applicable",
        description: "Not applicable",
      });
    } else if (decision === "pass" && issues.length === 0) {
      issues.push({
        issueCategory: "test_outcome",
        issueSubcategory: "no_issues_found",
        description: "No issues found",
      });
    }

    return {
      id: `attr-test-id-${summary.id}`,
      checkId: null,
      evalColumnId: column.id,
      reviewerType: summary.reviewerType,
      userId: summary.userId || null,
      decision,
      reason: "",
      reviewOrder: summary.reviewOrder,
      lockedBy: summary.lockedBy || null,
      issues,
      citations: [],
      columnVersion: summary.columnVersion,
      runId: summary.runId ?? null,
      createdAt: summary.createdAt,
      updatedAt: summary.updatedAt,
    };
  }

  // Inconclusive — map to fail with performance-issue badges.
  const issues: CheckResult["issues"] =
    summary.performanceIssues.length > 0
      ? summary.performanceIssues.map((issue) => ({
          issueCategory: "test_outcome",
          issueSubcategory: issue,
          description:
            PERFORMANCE_ISSUE_DESCRIPTIONS[issue] || "Testing incomplete",
        }))
      : [
          {
            issueCategory: "test_outcome",
            // No stated reason is still inconclusive, not a failure: keep this
            // off "issues_found" so the cell reads Requires Attention.
            issueSubcategory: "inconclusive",
            description: "Testing could not be completed",
          },
        ];

  return {
    id: `attr-test-id-${summary.id}`,
    checkId: null,
    evalColumnId: column.id,
    reviewerType: summary.reviewerType,
    userId: summary.userId || null,
    decision: "fail",
    reason: "",
    reviewOrder: summary.reviewOrder,
    lockedBy: summary.lockedBy || null,
    issues,
    citations: [],
    columnVersion: summary.columnVersion,
    runId: summary.runId ?? null,
    createdAt: summary.createdAt,
    updatedAt: summary.updatedAt,
  };
}

/**
 * Converts attribute test inline citations to CheckResultCitation format.
 */
function mapAttributeTestCitationsToCheckResultCitations(
  inlineCitations: Citation[],
  attributeId: string | null | undefined,
): CheckResult["citations"] {
  return inlineCitations.map((citation, index) => ({
    id: citation.id,
    boundingBox: citation.boundingBox,
    pageNumber: citation.pageNumber,
    pageWidth: citation.pageWidth,
    pageHeight: citation.pageHeight,
    documentId: citation.documentId,
    documentElement: deserialiseCitationFromString(
      citation.encodedCitationString,
    )[1],
    encodedCitationString: citation.encodedCitationString,
    attributeIds: attributeId ? [attributeId] : [],
    shortSummary: citation.shortSummary,
    reasonSpan: null,
    citationOrder: index + 1,
    reperformanceStepId: citation.reperformanceStepId ?? null,
    attributeTestResultId: citation.attributeTestResultId ?? null,
    outcomeSpanStart: citation.outcomeSpanStart ?? null,
    outcomeSpanEnd: citation.outcomeSpanEnd ?? null,
    isKey: citation.isKey ?? null,
    timestampSeconds: citation.timestampSeconds ?? null,
    createdAt: citation.createdAt,
    updatedAt: citation.updatedAt,
  }));
}

/**
 * Converts a CheckResult back to AttributeTestColumnResult format
 * Used when updating annotations from the UI
 *
 * @param checkResult - The modified check result from the UI
 * @param originalResult - The original attribute test result to preserve structure
 * @returns Updated AttributeTestColumnResult
 *
 * TODO: This reverse translation has limitations:
 * - We don't know which reperformance step a citation belongs to
 * - Backend guidance needed on how to handle citation updates within steps
 * - For now, we preserve the original step structure and only update top-level fields
 */
export function checkResultToAttributeTestResult(
  checkResult: CheckResult,
  originalResult: AttributeTestColumnResult,
): AttributeTestColumnResult {
  // TODO: Implement proper citation distribution back to reperformance steps
  // For now, preserve the original result structure
  // This function will need backend guidance on:
  // 1. How to determine which step a citation belongs to
  // 2. Whether to create new steps for new citations
  // 3. How to handle step-level vs result-level citation updates

  // If the result is inconclusive, preserve that structure
  if (!originalResult.isConclusive) {
    return {
      ...originalResult,
      // Could potentially update partial_description from checkResult.reason
      // but we need to be careful not to lose the structured data
    };
  }

  // For conclusive results, we can update outcome based on decision
  return {
    ...originalResult,
    outcome:
      checkResult.decision === "pass"
        ? "pass"
        : checkResult.decision === "not_applicable"
          ? "not_applicable"
          : "fail",
    // TODO: Parse checkResult.reason back into outcome_description and potential_mitigating_factors
    // This is non-trivial because we concatenated them in the forward direction
  };
}

/**
 * Helper to determine if a column is an attribute test column.
 *
 * This must stay false for an analytics column, and must not be re-expressed in
 * terms of `isVerdictColumn`. `AddCitationModal` treats it as "this cell has
 * reperformance steps to anchor a citation to", and an analytics cell has none —
 * a workflow reaches its verdict over a population, not by reperforming a step.
 * Widening it would offer a step picker with nothing to pick.
 */
export function isAttributeTestColumn(column: ProcedureColumn): boolean {
  return (
    column.attributeTestColumn !== null &&
    column.attributeTestColumn !== undefined
  );
}

/**
 * Helper to determine if a column reaches a verdict on each sample — an LLM does
 * it for an attribute test, a published workflow for analytics.
 *
 * This is the predicate for surfaces that treat the two alike: a pass/fail/N/A
 * outcome, a per-cell lock, an exception count. It is deliberately *not* the
 * predicate for anything keyed on reperformance steps (see
 * `isAttributeTestColumn`) or on whether an assigned attribute is mandatory.
 */
export function isVerdictColumn(column: ProcedureColumn): boolean {
  return !!(column.attributeTestColumn || column.analyticsColumn);
}

/**
 * Helper to get the column type for display purposes
 */
export function getColumnType(column: ProcedureColumn): ColumnType | null {
  if (column.supportColumn) return "support";
  if (column.evaluatedColumn) return "evaluated";
  if (column.attributeTestColumn) return "attribute_test";
  if (column.analyticsColumn) return "analytics";
  return null;
}

/**
 * Helper to convert citations back to the Citation format
 * (for when we need to update citations in attribute test results)
 * TODO: This will be used when implementing reverse translation for annotation updates
 */
export function checkResultCitationToCitation(
  checkResultCitation: CheckResult["citations"][number],
): Citation {
  return {
    id: checkResultCitation.id,
    documentId: checkResultCitation.documentId,
    encodedCitationString: checkResultCitation.documentElement,
    shortSummary: checkResultCitation.shortSummary,
    boundingBox: checkResultCitation.boundingBox,
    pageNumber: checkResultCitation.pageNumber,
    pageWidth: checkResultCitation.pageWidth,
    pageHeight: checkResultCitation.pageHeight,
    citationOrder: checkResultCitation.citationOrder || null,
    createdAt: checkResultCitation.createdAt.toString(),
    updatedAt: checkResultCitation.updatedAt.toString(),
  };
}
